import { defineStore } from "pinia";
import { Container } from "aurelia-dependency-injection";

import eventBus from "../../../utilities/eventBus";
import { MessageService } from "../../../services/message-service";
import {
    IGetMessageResult,
    IMessageItem,
    IMessageReaction,
    IMessageUser,
    ISenderDetails,
    ISendMessageQuery
} from "../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { useConversationStore } from "./useConversationStore";
import { useEditorStore } from "./useEditorStore";
import { useApp } from "../../App/useApp";
import { formatTaggedUserMessage, sortPlaceholdersList } from "../../../common/vue-helpers/modifiers/format-message";
import {
    IMessageReactionNotification,
    INotification
} from "../../../resources-vue/vue-interfaces/messaging-service/i-notification";
import { useUserStore } from "./useUsersStore";
import { IUserStatusDetails } from "../../../resources-vue/vue-interfaces/messaging-service/i-user";

interface IMessageStore extends IMessageItem {
    deliveryStatus: string; // 'sending' | 'sent' | 'failed'
    errorMessage?: string;
}

interface IStorePagination {
    isAtEnd: boolean;
    isFetching: boolean;
    lastMessageId: string;
}

export const useMessagesStore = defineStore("messages", {
    state() {
        return {
            messages: {} as Record<string, IMessageItem>,
            conversationMessages: {},

            // pagination
            messagePagination: {} as Record<string, IStorePagination>
        };
    },

    getters: {
        getConversationMessages: (state) => {
            const convStore = useConversationStore();
            const ids = state.conversationMessages[convStore.activeConversationId] || [];

            return ids.map((id: string) => state.messages[id]);
        },

        getAreConvMsgsLoading: (state) => {
            return (convId: string) => {
                return state.messagePagination[convId] ? state.messagePagination[convId].isFetching : true;
            };
        }
    },

    actions: {
        async fetchMessages(convId: string, pageLength: number) {
            const msgs = this.conversationMessages[convId];
            if (msgs) {
                this.fetchNewerMessages(convId, true);
                return;
            }
            this.messagePagination[convId] = {
                isAtEnd: false,
                isFetching: true,
                lastMessageId: null
            };
            const messageService = Container.instance.get(MessageService);
            const response: IGetMessageResult = await messageService.getMessages(convId, {
                pageLength: pageLength
            });

            const messages = response.messages;
            this.messagePagination[convId].isAtEnd = response.isAtEnd;
            this.messagePagination[convId].lastMessageId = response.lastMessageId;

            this.conversationMessages[convId] = [];
            // Get conversation users
            const conversationUsers = await messageService.getConversationUsers(convId);
            messages.forEach((message: IMessageItem) => {
                // Start with an empty readList for every message
                message.readList = [];

                conversationUsers.users.items.forEach((user: any) => {
                    if (user.isCurrentParticipant) {
                        return;
                    }
                    const hasReadMessage =
                        !!user.lastReadTime &&
                        new Date(user.lastReadTime).getTime() >= new Date(message.createdOn).getTime();

                    if (hasReadMessage) {
                        message.readList.push(`${user.firstName} ${user.lastName}`);
                    }
                });

                this.messages[message.id] = message;
                if (!this.conversationMessages[convId].includes(message.id)) {
                    this.conversationMessages[convId].push(message.id);
                }
            });

            this.readAllPreviousMessages(convId);
            this.messagePagination[convId].isFetching = false;
        },

        async fetchNewerMessages(convId: string, markRead: boolean = false) {
            const idList = this.conversationMessages[convId];
            if (idList) {
                // This condition avoids fetching msgs if the conversation is not loaded previously
                const messageService = Container.instance.get(MessageService);
                const convStore = useConversationStore();
                if (convStore.conversations[convId]?.unread > 0 && markRead) {
                    await convStore.markConversationAsRead(convId);
                }

                const existingMsgIds = new Set(idList);
                const fromMessageId = this.conversationMessages[convId][idList.length - 1];

                const messageResult = await messageService.getNewerMessages(convId, {
                    fromMessageId,
                    pageLength: 25
                });

                // Get conversation users for read status
                let conversationUsers;

                if (messageResult.messages.length > 0) {
                    conversationUsers = await messageService.getConversationUsers(convId);
                }

                messageResult.messages.forEach((msg) => {
                    if (existingMsgIds.has(msg.id)) {
                        return;
                    }

                    // Start with empty readList for every new message
                    msg.readList = [];

                    conversationUsers?.users?.items.forEach((user: any) => {
                        if (user.isCurrentParticipant) {
                            return;
                        }
                        const hasReadMessage =
                            !!user.lastReadTime &&
                            new Date(user.lastReadTime).getTime() >= new Date(msg.createdOn).getTime();
                        if (hasReadMessage) {
                            msg.readList.push(`${user.firstName} ${user.lastName}`);
                        }
                    });

                    this.messages[msg.id] = {
                        ...msg,
                        isRead: msg.isAutoGenerated ? true : msg.isRead
                    };
                    this.conversationMessages[convId].push(msg.id);
                });
            }
        },

        async fetchOlderMessages(convId: string) {
            const pagination = this.messagePagination[convId];

            if (!pagination || pagination.isAtEnd) return;

            const messageService = Container.instance.get(MessageService);
            const idList = this.conversationMessages[convId];
            const existingMsgIds = new Set(idList);

            const messsageResult = await messageService.getMessages(convId, {
                fromMessageId: pagination.lastMessageId,
                pageLength: 25
            });

            const olderMsgIds = [];
            messsageResult.messages.forEach((msg) => {
                if (existingMsgIds.has(msg.id)) return;

                this.messages[msg.id] = msg;
                olderMsgIds.push(msg.id);
            });
            this.conversationMessages[convId] = [...olderMsgIds, ...this.conversationMessages[convId]];
        },

        async sendMessage(convId: string, payload: ISendMessageQuery, cacheMsg: IMessageStore) {
            const messageService = Container.instance.get(MessageService);
            cacheMsg.readList = [];

            // Add temp message - to show retry state if failed
            this.addMessage(convId, cacheMsg);

            const convStore = useConversationStore();

            try {
                const messagesResult = await messageService.sendMessage(convId, payload);
                // Fetch real message to get assetIds and replyToMessage details
                const msgDetails = (await messageService.getMessageById(cacheMsg.conversationId, messagesResult.id))
                    .messageData;
                msgDetails.isRead = true;

                // Remove temp message
                this.tempDeleteMessage(cacheMsg.id);

                // Add real message
                this.addMessage(convId, msgDetails);
                cacheMsg.id = msgDetails.id;
                await convStore.updateSideNavMessage(convId, cacheMsg);
                convStore.moveConversationToTop(convId);
            } catch (e: any) {
                console.error(e);
                this.updateMessage(cacheMsg.id, { deliveryStatus: "failed", errorMessage: e.message });
            }
        },

        async retryMessage(convId: string, messageId: string) {
            const message = this.messages[messageId];

            if (!message) return;

            this.updateMessage(messageId, { deliveryStatus: "sending", errorMessage: "" });

            try {
                let messageQuery: ISendMessageQuery = {
                    message: message.text,
                    isImportant: message.isImportant,
                    senderDisplayFirstname: message.senderDetails.firstName,
                    senderDisplayLastname: message.senderDetails.lastName,
                    replyToMessageId: message.replyToMessage.messageId,
                    placeHoldersList: message.placeHoldersList
                };

                const messageService = Container.instance.get(MessageService);

                const messagesResult = await messageService.sendMessage(message.conversationId, messageQuery);

                // remove previous failed message

                const msgDetails = (await messageService.getMessageById(message.conversationId, messagesResult.id))
                    .messageData;

                // Remove temp message
                this.tempDeleteMessage(messageId);

                // Add real message
                this.addMessage(convId, msgDetails);
            } catch (e: any) {
                console.error(e);

                this.updateMessage(messageId, { deliveryStatus: "failed", errorMessage: e.message });
            }
        },

        addMessage(convId: string, message: IMessageItem) {
            this.messages[message.id] = message;
            if (!this.conversationMessages[convId]) {
                this.conversationMessages[convId] = [];
            }
            this.conversationMessages[convId].push(message.id);

            eventBus.emit("scrollToBottom");
        },

        updateMessage(messageId: string, updates: Partial<IMessageStore>) {
            const message = this.messages[messageId];

            if (!message) return;

            this.messages[messageId] = { ...message, ...updates };
        },

        editMessage(notification: INotification) {
            // update side nav
            const convStore = useConversationStore();
            const appStore = useApp();
            const conv = convStore.conversations[notification.conversationId];
            if (conv && conv?.lastMessageId === notification.messageId) {
                const { taggedUsers } = sortPlaceholdersList(notification.placeholderList);
                const { text, showTagSignForMsg } = formatTaggedUserMessage(
                    notification.message,
                    taggedUsers,
                    appStore.loggedInUserDetails.loginId
                );

                convStore.updateConvDetails(notification.conversationId, {
                    ...conv,
                    text: text,
                    isCurrentUserTagged: showTagSignForMsg,
                    isImportant: notification.isImportant
                });
            }

            // update message
            const message = this.messages[notification.messageId];

            if (!message) return;
            this.messages[notification.messageId] = {
                ...message,
                text: notification.message,
                placeHoldersList: notification.placeholderList,
                isEdited: true,
                assetIds: notification.messageAssets,
                conversationId: notification.conversationId,
                isImportant: notification.isImportant
            };
        },

        tempDeleteMessage(messageId: string) {
            const message = this.messages[messageId];

            if (!message) return;

            const convStore = useConversationStore();
            const convId = convStore.activeConversationId;

            delete this.messages[messageId];

            this.conversationMessages[convId] = this.conversationMessages[convId].filter((id) => id !== messageId);
        },

        deleteMessage(messageId: string, convId: string) {
            // update side nav
            const convStore = useConversationStore();
            const conv = convStore.conversations[convId];
            if (conv && conv?.lastMessageId === messageId) {
                convStore.updateConvDetails(convId, { ...conv, text: "This message has been deleted" });
            }

            // update message
            const message = this.messages[messageId];
            if (!message) return;

            const now = new Date();
            message.text = "This message has been deleted";
            message.isDeprecated = true;
            message.modifiedOn = new Date(now.getTime() + now.getTimezoneOffset() * 60 * 1000);
        },

        async undoDeleteMessage(messageId: string, convId: string) {
            // update message
            const messageService = Container.instance.get(MessageService);

            const msgDetails = (await messageService.getMessageById(convId, messageId)).messageData;
            if (this.messages[messageId]) {
                this.messages[messageId] = msgDetails;
            }

            // update side nav
            const convStore = useConversationStore();
            const conv = convStore.conversations[convId];
            if (conv && conv?.lastMessageId === messageId) {
                convStore.updateSideNavMessage(convId, msgDetails);
            }
        },

        async receivedNewMsgInActiveConv(notification: INotification) {
            const editorStore = useEditorStore();
            let senderInfo: ISenderDetails = null;
            if (notification.senderName) {
                senderInfo = {
                    firstName: notification.senderName.split(" ")[0],
                    lastName: notification.senderName.split(" ")[1]
                };
            }

            const newMessage = {
                id: notification.messageId,
                text: notification.messageBody,
                isRead: true,
                isEdited: false,
                autoMessageType: notification.autoMessageType,
                isImportant: notification.isImportant,
                isAutoGenerated: notification.isAutoGenerated,
                isFromSelf: false,
                createdOn: new Date().toISOString().slice(0, -1),
                modifiedOn: new Date().toISOString().slice(0, -1),
                senderUserId: notification.senderId,
                isLoading: false,
                isError: false,
                assetIds: (notification.messageAssets as string[]) || [],
                replyToMessage: notification.replyToMessageId
                    ? await editorStore.fetchMessageFromId(notification.replyToMessageId, notification.conversationId)
                    : null,
                senderDetails: senderInfo,
                placeHoldersList: notification.placeholders || [],
                conversationId: notification.conversationId,
                isDeprecated: false
            };

            this.addMessage(notification.conversationId, newMessage);
        },

        readAllPreviousMessages(convId: string) {
            const msgIds = this.conversationMessages[convId];
            if (!msgIds.length) return;

            msgIds.forEach((id: string) => {
                this.messages[id].isRead = true;
            });
        },

        updateMessageReactions(notification: IMessageReactionNotification) {
            const { messageId, emoji, messageReactionAction, userId } = notification;
            const message = this.messages[messageId];
            if (!message) return;
            if (!message.messageReactions) message.messageReactions = {};
            const reactions = message.messageReactions[emoji] || [];
            if (messageReactionAction === 1) {
                const appStore = useApp();
                let senderDetails: IMessageUser | null = null;
                if (appStore.loggedInUserDetails?.userId === userId) {
                    senderDetails = {
                        firstName: appStore.loggedInUserDetails.firstName,
                        lastName: appStore.loggedInUserDetails.lastName
                    };
                } else {
                    const userStore = useUserStore();
                    const loginId = userStore.getLoginIdByUserId(userId);
                    senderDetails = userStore.getUser(loginId) || null;
                }
                if (!reactions.some((r) => r.senderUserId === userId)) {
                    const newReaction: IMessageReaction = {
                        id: crypto.randomUUID(),
                        messageId,
                        emoji,
                        senderUserId: userId,
                        senderDetails,
                        createdOn: new Date().toISOString()
                    };
                    message.messageReactions[emoji] = [...reactions, newReaction];
                }
            }
            if (messageReactionAction === 3) {
                const updated = reactions.filter((r) => r.senderUserId !== userId);
                if (updated.length === 0) {
                    delete message.messageReactions[emoji];
                } else {
                    message.messageReactions[emoji] = updated;
                }
            }
        },

        markMessagesAsRead(convId: string) {
            const ids = this.conversationMessages[convId] || [];
            ids.forEach((id: string) => (this.messages[id].isRead = true));
        },
        addReadUser(notification: any) {
            const convMsgs = this.conversationMessages[notification.conversationId];

            if (!convMsgs || convMsgs.length === 0) {
                return;
            }

            // Get latest message
            const reversedArray = convMsgs.toReversed();
            const msgId = reversedArray[0];

            const msg = this.messages[msgId];

            if (!msg) {
                return;
            }

            const messageCreatedTime = new Date(msg.createdOn).getTime();
            const notificationReadTime = new Date(notification.readTime).getTime();
            const hasReadMessage = notificationReadTime >= messageCreatedTime;
            if (!hasReadMessage) {
                return;
            }

            const userStore = useUserStore();
            const user = userStore.getUser(notification.loginId);

            if (!user) {
                return;
            }

            if (!msg.readList) {
                msg.readList = [];
            }

            const username = `${user.firstName} ${user.lastName}`;

            if (!msg.readList.includes(username)) {
                msg.readList.push(username);
            }
        }
    }
});
